import { GENERATED_ROLE_DEFAULTS_VERSION } from '@revelio/data-access/role-defaults';

import {
  PageType,
  type ReportPageConfig,
} from '../../components/PageConfigProvider/types';
import { FilterId } from '../../filters/types/filters';
import {
  buildFilterGroups,
  encodeFilterGroups,
} from '../../filters/utils/filterGroupsUtils';
import { buildReportPages } from './report-page-builder';

const companyParam = { parameterId: 'company-parameter', grouped: false };
const competitorsGrouped = {
  parameterId: 'competitors-parameter',
  grouped: true,
};
const competitorsUngrouped = {
  parameterId: 'competitors-parameter',
  grouped: false,
};
const geographyParam = { parameterId: 'geography-parameter', grouped: false };
const roleParam = { parameterId: 'role-parameter', grouped: false };

const industryOverviewPlotParameters = [
  competitorsUngrouped,
  geographyParam,
  roleParam,
];

const salaryTablePlotParameters = [
  companyParam,
  competitorsGrouped,
  geographyParam,
  roleParam,
];

const competitorBoxPlotParameters = [
  companyParam,
  competitorsUngrouped,
  geographyParam,
  roleParam,
];

export const reportCompanyCompensationBenchmark: ReportPageConfig = (() => {
  const { items, grid } = buildReportPages('company-compensation-benchmark', [
    [
      {
        id: 'about-text',
        title: '',
        position: { height: 32 },
        plot: {
          chartType: 'ai-summary',
          textHtml: '## About\nDescription',
        },
      },
    ],
    [
      {
        id: 'comp-bench-industry-overview-text',
        title: '',
        position: { width: 26, height: 21 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Industry overview\n\nBenchmarking a company\'s compensation against its peers requires careful consideration of comparable organizations. We select these peer companies using our competitors model, which accounts for factors such as industry and company size.\n\nHere, we show an overview of the peer group selected. We select the 50 closest companies to {{PARAM "Company"}} from our competitors model and weight them relative to their current employee headcounts.\n\nWe observe around {{FORMAT (SUM "headcount") format="SI"}} {{filter "ROLE"}} positions in {{filter "GEOGRAPHY"}} currently employed in one of these peer companies. In the peer group, {{ARG_MAX "headcount"}} has the largest number of employees, with {{FORMAT (SHARE "headcount" entity=(ARG_MAX "headcount")) format="PERCENTAGE"}} of the total headcount.',
          plotParameters: industryOverviewPlotParameters,
        },
      },
      {
        id: 'comp-bench-industry-treemap',
        title: 'Composition of {{PARAM "Company"}}\'s peer group',
        position: { x: 26, y: 2, width: 22, height: 19 },
        plot: {
          chartType: 'treemap',
          groupBySegment: false,
          defaultMetricOptions: ['headcount'],
          plotParameters: [competitorsUngrouped],
        },
      },
    ],
    [
      {
        id: 'comp-bench-salary-intro',
        title: '',
        position: { height: 7 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Salary distribution compared to peer group\n\nUnderstanding the salary distribution of a company relative to its peer group is essential in compensation benchmarking. Here, we show how the salary and total compensation at {{PARAM "Company"}} compares to its peers.',
        },
      },
      {
        id: 'comp-bench-salary-table',
        title: 'Salary of current employees in role and geography selected',
        position: { y: 7, height: 9 },
        plot: {
          chartType: 'table',
          defaultMetricOptions: ['base_salary_percentiles'],
          tableColumns: [
            {
              id: 'comp-bench-salary-table-company',
              kind: 'dimension',
              dimension: 'company',
              alignment: 'left',
            },
            {
              id: 'comp-bench-salary-table-base-salary',
              kind: 'variable',
              metric: 'base_salary_percentiles',
              aggregation: 'sum',
            },
          ],
          enableRowSelection: false,
          plotParameters: salaryTablePlotParameters,
        },
        isTitleManuallySet: true,
      },
      {
        id: 'e1a002c8-5bcf-409d-a6ed-a7381d604b9c',
        title:
          'Total compensation of current employees in role and geography selected',
        position: { y: 16, height: 9 },
        plot: {
          chartType: 'table',
          enableRowSelection: false,
          plotParameters: salaryTablePlotParameters,
          defaultMetricOptions: ['total_compensation_percentiles'],
          tableColumns: [
            {
              id: 'comp-bench-total-comp-table-company',
              kind: 'dimension',
              dimension: 'company',
              alignment: 'left',
            },
            {
              id: 'comp-bench-total-comp-table-total-comp',
              kind: 'variable',
              metric: 'total_compensation_percentiles',
              aggregation: 'sum',
            },
          ],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'comp-bench-box-intro',
        title: '',
        position: { height: 10 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Salary distribution compared to peer group\n\nThere can be pros and cons of having higher variation in salaries even within the same role or geographical area in a company. For example, a wide range of salaries in a company may be the result of employers offering competitive compensation packages to attract and retain top talent. However, a range that is too wide may create perceptions of inequality within the organization, leading to conflict or decreased morale if employees believe that others are unfairly compensated for similar levels of work or contribution.\n\nHere, we see how {{PARAM "Company"}} compares to its peer group in terms of salary distribution.',
        },
      },
      {
        id: 'comp-bench-box-plot',
        title:
          'Salary distribution of {{filter "ROLE"}} in {{filter "GEOGRAPHY"}}',
        info: {
          text: '10th, 25th, 50th, 75th, and 90th percentiles of current employee salaries in role and geography selected',
        },
        position: { x: 8, y: 10, width: 32, height: 9 },
        plot: {
          chartType: 'box',
          defaultMetricOptions: ['base_salary'],
          showQuartiles: true,
          plotParameters: salaryTablePlotParameters,
          plotFilters: [],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'comp-bench-competitor-intro',
        title: '',
        position: { height: 14 },
        plot: {
          chartType: 'text',
          textHtml: `## Salary distribution compared to competitors\n\nCompensation benchmarking allows employers to assess the competitiveness of their compensation package relative to those of its peers.\n\nWhile comparing salaries between employees at a company and its peer group as a whole provides a high-level view of where a company's compensation package stands, Revelio Labs' rich workforce data allows for a more detailed company-level comparison as well. This enables organizations to determine which specific competitors they could recruit talent from, which they might be in danger of losing talent to, and how they can set compensation to match or exceed certain companies.\n\nHere, we compare {{PARAM "Company"}}'s salaries to those of its fifteen closest competitors. Companies paying lower salaries than their peers may benefit from strategically considering their compensation packages if they seek to increase employee retention and satisfaction.`,
        },
      },
      {
        id: 'comp-bench-competitor-box',
        title:
          'Salary distribution of {{filter "ROLE"}} in {{filter "GEOGRAPHY"}}',
        info: {
          text: '10th, 25th, 50th, 75th, and 90th percentiles of current employee salaries in role and geography selected',
        },
        position: { x: 1, y: 14, width: 47, height: 18 },
        plot: {
          chartType: 'box',
          defaultMetricOptions: ['base_salary'],
          showQuartiles: true,
          plotParameters: competitorBoxPlotParameters,
          plotFilters: [],
        },
        isTitleManuallySet: true,
      },
    ],
  ]);

  return {
    id: 'report-company-compensation-benchmark',
    name: 'Compensation Benchmark',
    type: PageType.REPORT,
    owner: {
      id: 'db176d03-a841-44ee-bedb-78b2aa87c32a',
      username: 'jeremie@reveliolabs.com',
    },
    sampleFilterGroups: encodeFilterGroups(
      buildFilterGroups(
        [
          {
            groupKey: 'company-parameter',
            filterIds: [FilterId.COMPANY],
            defaultSelections: {
              [FilterId.COMPANY]: [
                {
                  id: '8_60_1359692',
                  name: 'Amazon.com, Inc.',
                  levelId: 'company',
                  isCurrent: true,
                },
              ],
            },
          },
          {
            groupKey: 'competitors-parameter',
            filterIds: [FilterId.COMPANY],
            defaultSelections: {
              [FilterId.COMPANY]: [
                {
                  id: '8_60_832518',
                  name: 'The Trade Desk, Inc.',
                  levelId: 'company',
                  isCurrent: true,
                },
                {
                  id: '8_57_22148737',
                  name: 'HubSpot, Inc.',
                  levelId: 'company',
                  isCurrent: true,
                },
                {
                  id: '8_60_1370',
                  name: 'Pinterest, Inc.',
                  levelId: 'company',
                  isCurrent: true,
                },
                {
                  id: '8_60_929633',
                  name: 'Groupon, Inc.',
                  levelId: 'company',
                  isCurrent: true,
                },
                {
                  id: '8_59_857255',
                  name: 'Chewy, Inc.',
                  levelId: 'company',
                  isCurrent: true,
                },
                {
                  id: '0_0_22149845',
                  name: 'Coupang Co. Ltd.',
                  levelId: 'company',
                  isCurrent: true,
                },
                {
                  id: '8_60_1114699',
                  name: 'Shopify, Inc.',
                  levelId: 'company',
                  isCurrent: true,
                },
                {
                  id: '0_0_77107',
                  name: 'Cash App Investing LLC',
                  levelId: 'company',
                  isCurrent: true,
                },
                {
                  id: '8_57_167546',
                  name: 'Rakuten Group, Inc.',
                  levelId: 'company',
                  isCurrent: true,
                },
              ],
            },
          },
          {
            groupKey: 'geography-parameter',
            filterIds: [FilterId.GEOGRAPHY],
            defaultSelections: {
              [FilterId.GEOGRAPHY]: [
                { id: '1_1', name: 'United States', levelId: 'country' },
              ],
            },
          },
          {
            groupKey: 'role-parameter',
            filterIds: [FilterId.ROLE, FilterId.ROLE_TAXONOMY],
            defaultSelections: {
              [FilterId.ROLE]: [
                {
                  id: '7_134',
                  name: 'Software Engineer',
                  levelId: 'role_k150',
                },
              ],
            },
          },
        ],
        {
          primaryFilterId: FilterId.COMPANY,
          taxonomyConfig: {
            roleTaxonomyVersion: GENERATED_ROLE_DEFAULTS_VERSION,
          },
        }
      )
    ),
    tabs: [
      {
        label: 'In-depth analysis of role compensation vs industry peers',
        id: 'default',
        isActive: true,
        tabContent: {
          id: 'company-compensation-benchmark',
          title: 'Compensation Benchmarking',
          description:
            "Benchmarking a company's salary and compensation structure to its peers provides insights into its ability to acquire and retain talent, its competitiveness in the labor market, and its pay equity.",
          grid,
          items,
          primaryFilter: {
            id: FilterId.COMPANY,
          },
          secondaryFilters: [],
          reportParameters: [
            {
              parameterId: 'company-parameter',
              filterId: FilterId.COMPANY,
              isPrimary: true,
              name: 'Company',
            },
            {
              parameterId: 'competitors-parameter',
              filterId: FilterId.COMPANY,
              isPrimary: false,
              name: 'Company Competitors',
              selectionLimit: 50,
            },
            {
              parameterId: 'geography-parameter',
              filterId: FilterId.GEOGRAPHY,
              isPrimary: false,
              name: 'Geography',
            },
            {
              parameterId: 'role-parameter',
              filterId: FilterId.ROLE,
              isPrimary: false,
              name: 'Role',
            },
          ],
        },
      },
    ],
  };
})();
